import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";
import banner1 from "../assets/home/banner1.png";

function Icon({ children }) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}

const ShieldIcon = () => (
  <Icon>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </Icon>
);

const AwardIcon = () => (
  <Icon>
    <circle cx="12" cy="8" r="7" />
    <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
  </Icon>
);

const ClockIcon = () => (
  <Icon>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </Icon>
);

const UsersIcon = () => (
  <Icon>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Icon>
);

const FACTS = [
  { title: "Manchester, UK", text: "Where we are based" },
  { title: "Retail and wholesale", text: "Who we serve" },
  { title: "Premium dry African foods", text: "What we supply today" },
];

const WHY = [
  {
    title: "Quality Assurance",
    text: "Every product is carefully selected to ensure it meets our high standards.",
    Icon: ShieldIcon,
  },
  {
    title: "Authenticity",
    text: "We source products that maintain the true flavors and quality of African cuisine.",
    Icon: AwardIcon,
  },
  {
    title: "Reliability",
    text: "Our Manchester-based location allows for fast, consistent delivery to our customers.",
    Icon: ClockIcon,
  },
  {
    title: "Customer-Centric",
    text: "Whether you are a shop, restaurant, or individual, we are committed to providing exceptional service and support.",
    Icon: UsersIcon,
  },
];

// Real photos of the products on sale, so the page looks like a grocery shop
// (and the pictures stay up to date as you add products).
function HeroImages({ images, loading }) {
  if (loading) {
    return (
      <div className="ab-mosaic" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="ab-tile ab-tile--loading" />
        ))}
      </div>
    );
  }

  if (images.length < 2) {
    return (
      <div className="ab-hero-fallback">
        <img src={banner1} alt="Aquaviva Foods products" />
      </div>
    );
  }

  const shown = images.length >= 4 ? 4 : 2;

  return (
    <div className="ab-mosaic">
      {images.slice(0, shown).map((product) => (
        <div key={product.id} className="ab-tile">
          <img src={product.image_url} alt={product.name} loading="lazy" />
        </div>
      ))}
    </div>
  );
}

export default function About() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const { data, error } = await supabase
        .from("products")
        .select("id, name, image_url")
        .not("image_url", "is", null)
        .gt("stock", 0)
        .order("created_at", { ascending: false })
        .limit(4);

      if (cancelled) return;
      setImages(error ? [] : data ?? []);
      setLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="about">
      {/* HERO */}
      <section className="ab-hero">
        <div>
          <h1 className="ab-title">About Aquaviva Foods</h1>
          <p className="ab-lead">Authentic African Flavors, Delivered Across the UK</p>
          <p className="ab-hero-text">
            Your trusted source for premium African food products in the United Kingdom, for
            homes and businesses alike.
          </p>
          <div className="ab-actions">
            <Link to="/products" className="ui-btn ui-btn--primary">Shop our products</Link>
            <Link to="/contact" className="ui-btn ui-btn--ghost">Get in touch</Link>
          </div>
        </div>

        <HeroImages images={images} loading={loading} />
      </section>

      <section className="ab-facts" aria-label="At a glance">
        {FACTS.map((fact) => (
          <div key={fact.title} className="ab-fact">
            <strong>{fact.title}</strong>
            <span>{fact.text}</span>
          </div>
        ))}
      </section>

      {/* WHO WE ARE */}
      <section className="ab-section ab-split">
        <h2 className="ab-h2">Who we are</h2>
        <div className="ab-prose">
          <p>
            Welcome to Aquaviva Foods UK – your trusted source for premium African food products in
            the United Kingdom. At Aquaviva Foods, we are passionate about bringing the rich
            flavors, quality, and authenticity of African cuisine to homes and businesses across
            the UK.
          </p>
          <p>
            Founded and registered in the UK, our company is based in Manchester, strategically
            located to serve both retail and wholesale customers efficiently.
          </p>
        </div>
      </section>

      {/* MISSION */}
      <section className="ab-section ab-mission">
        <h2 className="ab-h2">Our mission</h2>
        <p>
          Our mission is to source, supply, and deliver high-quality African food products that
          meet the expectations of discerning customers. We aim to make authentic ingredients
          easily accessible, helping our clients recreate the taste of Africa in every meal.
        </p>
      </section>

      {/* WHAT WE DO */}
      <section className="ab-section ab-split">
        <h2 className="ab-h2">What we do</h2>
        <div className="ab-prose">
          <p>
            Currently, our focus is on premium dry African food products, carefully sourced to
            ensure authenticity, quality, and freshness. We work closely with trusted suppliers to
            provide products that reflect the diverse culinary traditions of Africa.
          </p>
          <p>
            In the near future, we plan to expand our offerings to include fresh vegetables and
            other African food items, supplying directly to African shops, restaurants, and
            markets across the UK.
          </p>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="ab-section" aria-labelledby="why-title">
        <h2 id="why-title" className="ab-h2" style={{ marginBottom: "1.1rem" }}>
          Why choose Aquaviva Foods?
        </h2>
        <div className="ab-why">
          {WHY.map(({ title, text, Icon: WhyIcon }) => (
            <div key={title} className="home-offer">
              <span className="home-offer-icon"><WhyIcon /></span>
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CLOSING */}
      <section className="ab-cta">
        <p>
          At Aquaviva Foods UK, we are more than just a supplier – we are a partner in celebrating
          African food, culture, and community. Join us on our journey to bring premium African
          products to your table.
        </p>
        <p>Get in touch to find out how we can help you with your supplies.</p>
        <div className="ab-actions" style={{ justifyContent: "center" }}>
          <Link to="/contact" className="ui-btn ui-btn--primary">Contact us</Link>
          <Link to="/products" className="ui-btn ui-btn--ghost">Browse products</Link>
        </div>
      </section>
    </div>
  );
}